import React, { useState, useEffect, useCallback } from "react";
import { Eye, Save, Plus, Copy, Trash2, Star } from "lucide-react";
import api, { BASE } from "../api";
import { Cd, Bt, Inp } from "../components";
import { RichTextEditor } from "../components/RichTextEditor";
import { notify, confirmDialog } from "../notify";

// Document templates: offer letter, resignation, relieving and experience
// letters. Each type keeps several templates; one is the default. HR edits them
// with the rich-text editor and previews them as they will be sent.
const PLACEHOLDER_HELP = {
  candidate_name: "Candidate's name",
  candidate_address_block: "Candidate's address (optional block)",
  designation: "Job title",
  company_name: "Company name",
  location: "Office city",
  ctc: "Annual CTC (₹)",
  ctc_words: "CTC in words",
  join_date: "Joining date",
  offer_date: "Date of the letter (today)",
  stamp_image: "Authorised signatory stamp",
  document_checklist: "Joining document checklist",
};

// Opens a PDF of a template body in a new tab, laid out like the sent document.
// Nothing is saved. Pass candidateId for offers or employeeId for exit letters
// to preview with real details; otherwise sample values are used.
async function openPreview(bodyHtml, candidateId = null, docType = "offer_letter", employeeId = null) {
  try {
    const res = await fetch(`${BASE}/api/offer-templates/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${api.getToken()}` },
      body: JSON.stringify({ body_html: bodyHtml, doc_type: docType, candidate_id: candidateId, employee_id: employeeId }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || "Preview failed");
    }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
  } catch (e) {
    notify(e.message || "Preview failed", { title: "Error", variant: "error" });
  }
}

export const OfferTemplatesTab = () => {
  const [docType, setDocType] = useState("offer_letter");
  const [docTypes, setDocTypes] = useState([]);
  const [fields, setFields] = useState([]);
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback((type = docType) => {
    return api.get(`/api/offer-templates?doc_type=${type}`).then(d => {
      if (!d) return;
      setList(d.data || []);
      setFields(d.fields || []);
      setDocTypes(d.doc_types || []);
    }).catch(() => {});
  }, [docType]);
  useEffect(() => { load(docType); }, [docType, load]);

  const current = list.find(t => t.id === selectedId) || null;

  const resetEditor = () => { setSelectedId(null); setName(""); setBody(""); setCreating(false); setEditorKey(k => k + 1); };

  const switchType = (t) => {
    setDocType(t);
    resetEditor();
  };

  const pick = (t) => {
    setCreating(false);
    setSelectedId(t.id);
    setName(t.name);
    setBody(t.body_html);
    setEditorKey(k => k + 1);
  };

  const newBlank = () => {
    setCreating(true);
    setSelectedId(null);
    setName("");
    setBody("");
    setEditorKey(k => k + 1);
  };

  const save = async (asNew = false) => {
    if (!name.trim()) return notify("Give the template a name", { title: "Check", variant: "error" });
    if (!body.trim()) return notify("The template is empty", { title: "Check", variant: "error" });
    setBusy(true);
    try {
      const payload = { name: name.trim(), body_html: body, doc_type: docType };
      const r = (selectedId && !asNew)
        ? await api.put(`/api/offer-templates/${selectedId}`, payload)
        : await api.post("/api/offer-templates", payload);
      notify(asNew || !selectedId ? "Template saved" : "Template updated", { title: "Saved", variant: "success" });
      await load(docType);
      if (r?.id) { setSelectedId(r.id); setCreating(false); }
    } catch (e) {
      notify(e.message || "Save failed", { title: "Error", variant: "error" });
    } finally { setBusy(false); }
  };

  const makeDefault = async () => {
    if (!current) return;
    try {
      await api.post(`/api/offer-templates/${current.id}/default`, {});
      notify(`"${current.name}" is now the default`, { title: "Saved", variant: "success" });
      await load(docType);
    } catch (e) { notify(e.message || "Could not set the default", { title: "Error", variant: "error" }); }
  };

  const remove = async () => {
    if (!current) return;
    if (current.is_default) return notify("This is the default. Set another template as default first.", { title: "Check", variant: "error" });
    if (!(await confirmDialog(`Delete the template "${current.name}"? Documents already issued are not affected.`))) return;
    try {
      await api.del(`/api/offer-templates/${current.id}`);
      notify("Template deleted", { title: "Deleted", variant: "success" });
      resetEditor();
      await load(docType);
    } catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
  };

  const typeLabel = docTypes.find(d => d.key === docType)?.label || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-[6px]">
        {docTypes.map(d => (
          <button key={d.key} onClick={() => switchType(d.key)} className={`px-3 py-[6px] rounded-[8px] text-[12.5px] font-semibold border cursor-pointer ${docType === d.key ? "bg-p text-white border-p" : "bg-bg1 text-t1 border-b0 hover:bg-bg2"}`}>
            {d.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(220px, 260px) 1fr" }}>
        <Cd>
          <div className="text-[14px] font-bold text-t0 mb-3">{typeLabel} templates</div>
          <div className="flex flex-col gap-[6px] mb-3">
            {list.map(t => (
              <button key={t.id} onClick={() => pick(t)} className={`text-left px-3 py-2 rounded-[8px] border cursor-pointer ${selectedId === t.id ? "border-p bg-bg2" : "border-b0 bg-bg1 hover:bg-bg2"}`}>
                <div className="flex items-center gap-1 text-[13px] font-semibold text-t0">
                  {t.is_default && <Star size={12} color="#D97706" fill="#D97706" />}
                  {t.name}
                </div>
                <div className="text-[11px] text-t3">{t.is_default ? "Default — used unless another is chosen" : "Custom template"}</div>
              </button>
            ))}
            {list.length === 0 && <div className="text-[12px] text-t3">No templates yet.</div>}
          </div>
          <Bt v="secondary" s="sm" icon={Copy} onClick={newBlank}>New blank template</Bt>
        </Cd>

        <Cd>
          {!creating && !selectedId ? (
            <div className="text-[13px] text-t3 py-10 text-center">Pick a {typeLabel.toLowerCase()} template on the left, or start a new blank one.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[12px] text-t2 mb-1">Template name</div>
                  <Inp value={name} onChange={setName} placeholder="e.g. Senior role" />
                </div>
                <Bt v="secondary" s="sm" icon={Eye} onClick={() => openPreview(body, null, docType)}>Preview PDF</Bt>
                <Bt s="sm" icon={Save} onClick={() => save(false)} disabled={busy}>{selectedId ? "Save changes" : "Save template"}</Bt>
                {selectedId && <Bt v="secondary" s="sm" icon={Plus} onClick={() => save(true)} disabled={busy}>Save as new</Bt>}
                {current && !current.is_default && <Bt v="secondary" s="sm" icon={Star} onClick={makeDefault}>Set as default</Bt>}
                {current && !current.is_default && <Bt v="ghost" s="sm" icon={Trash2} onClick={remove}>Delete</Bt>}
              </div>

              <RichTextEditor
                key={editorKey}
                value={body}
                onChange={setBody}
                mergeFields={fields.map(f => f.key)}
                fieldFormat={(f) => `{{${f}}}`}
                minHeight={420}
              />
              <div className="text-[11px] text-t3 mt-2">Pick a field above and click Insert to place it at the cursor. The letterhead, footer and page layout are added automatically.</div>
            </>
          )}
        </Cd>
      </div>
    </div>
  );
};

export { openPreview, PLACEHOLDER_HELP };
